import { DEFAULT_LAYOUT_CONFIG } from "@/utils/panelLayoutCalculator";
import {
  clipRememberedVisibility,
  layoutWithPanelLimit,
  roofMeshLayoutForIndices,
  type PanelMount,
  type PanelVisibilityState,
  type PlacedRoofPanel,
  type RoofMeshLayout,
} from "@/utils/roofMeshPanelPlacement";

const STORAGE_KEY = "helio_roof_panel_layout";

function meshKey(meshUrl: string): string {
  const path = meshUrl.split("?")[0];
  try {
    return new URL(path, "http://localhost").pathname;
  } catch {
    return path;
  }
}

export interface StoredMountLayout extends PanelVisibilityState {
  dirty: boolean;
  /** Indices on the roof, oldest first. The stepper trims or extends this list. */
  shown: number[];
  panels: PlacedRoofPanel[];
  facetCounts: number[];
  totalKwp: number;
  minPanelY?: number;
}

export interface StoredPanelLayoutSession {
  meshUrl: string;
  lastMount?: PanelMount;
  portrait?: StoredMountLayout;
  landscape?: StoredMountLayout;
}

function facetCountsForPanels(panels: PlacedRoofPanel[], facetSlots: number): number[] {
  const counts = Array.from({ length: Math.max(facetSlots, 1) }, () => 0);
  for (const panel of panels) {
    while (counts.length <= panel.facetIndex) counts.push(0);
    counts[panel.facetIndex] += 1;
  }
  return counts;
}

export function mergeStoredMountLayout(
  baseline: RoofMeshLayout,
  stored: StoredMountLayout,
): RoofMeshLayout | null {
  if (!stored.dirty) return null;
  if (stored.panels.length < baseline.panels.length) return null;
  const panels = stored.panels;
  return {
    panels,
    facetCounts: facetCountsForPanels(panels, baseline.facetCounts.length),
    totalKwp: (panels.length * DEFAULT_LAYOUT_CONFIG.panelWatts) / 1000,
    minPanelY: stored.minPanelY ?? baseline.minPanelY,
  };
}

function readRaw(): StoredPanelLayoutSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredPanelLayoutSession>;
    if (typeof parsed.meshUrl !== "string" || !parsed.meshUrl.trim()) return null;
    return parsed as StoredPanelLayoutSession;
  } catch {
    return null;
  }
}

export function readPanelLayoutSession(meshUrl: string): StoredPanelLayoutSession | null {
  const stored = readRaw();
  if (!stored) return null;
  if (meshKey(stored.meshUrl) !== meshKey(meshUrl)) return null;
  return stored;
}

export function clearPanelLayoutSession(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private mode or quota.
  }
}

export function savePanelLayoutSession(
  meshUrl: string,
  mount: PanelMount,
  layout: RoofMeshLayout,
  state: PanelVisibilityState & { dirty: boolean; shown?: number[] },
): void {
  if (!state.dirty) return;
  try {
    const existing = readRaw();
    const next: StoredPanelLayoutSession = existing && meshKey(existing.meshUrl) === meshKey(meshUrl)
      ? { ...existing, meshUrl }
      : { meshUrl };
    const payload: StoredMountLayout = {
      dirty: true,
      panels: layout.panels,
      facetCounts: layout.facetCounts,
      totalKwp: layout.totalKwp,
      minPanelY: layout.minPanelY,
      removed: state.removed,
      priority: state.priority,
      shown: state.shown ?? state.priority,
    };
    if (mount === "portrait") next.portrait = payload;
    else next.landscape = payload;
    next.lastMount = mount;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Private mode or quota.
  }
}

export function storedMountLayout(
  session: StoredPanelLayoutSession | null,
  mount: PanelMount,
): StoredMountLayout | null {
  if (!session) return null;
  return mount === "portrait" ? session.portrait ?? null : session.landscape ?? null;
}

export function sessionMountLayout(
  meshUrl: string | null | undefined,
  mount: PanelMount,
  baseline: RoofMeshLayout | null,
): { layout: RoofMeshLayout; state: PanelVisibilityState & { shown: number[] }; dirty: boolean } | null {
  if (!meshUrl || !baseline) return null;
  const session = readPanelLayoutSession(meshUrl);
  const stored = storedMountLayout(session, mount);
  if (!stored) return null;
  const layout = mergeStoredMountLayout(baseline, stored);
  if (!layout) return null;
  return {
    layout,
    state: {
      removed: stored.removed ?? [],
      priority: stored.priority ?? [],
      shown: stored.shown ?? [],
    },
    dirty: stored.dirty,
  };
}

export function preferredSessionMount(meshUrl: string | null | undefined): PanelMount {
  if (!meshUrl) return "portrait";
  return readPanelLayoutSession(meshUrl)?.lastMount ?? "portrait";
}

/** Satellite and 2D tab read the same session snapshot as the 3D viewer. */
export function visibleLayoutForPackage(
  baseline: RoofMeshLayout,
  meshUrl: string | null | undefined,
  mount: PanelMount,
  limit: number | null | undefined,
): RoofMeshLayout {
  const session = sessionMountLayout(meshUrl, mount, baseline);
  const full = session?.layout ?? baseline;
  if (!session?.dirty) return layoutWithPanelLimit(full, limit);
  const { shown } = clipRememberedVisibility(full.panels.length, limit, session.state);
  return roofMeshLayoutForIndices(full, shown);
}

/** Apply a stored session layout onto the in-memory viewer state, if one exists. */
export function hydrateLayoutFromSession(
  meshUrl: string | null | undefined,
  mount: PanelMount,
  baseline: RoofMeshLayout | null | undefined,
): {
  layout: RoofMeshLayout;
  state: PanelVisibilityState & { dirty: boolean; shown: number[] };
} | null {
  const restored = sessionMountLayout(meshUrl, mount, baseline ?? null);
  if (!restored?.dirty) return null;
  return {
    layout: restored.layout,
    state: {
      dirty: true,
      removed: restored.state.removed,
      priority: restored.state.priority,
      shown: restored.state.shown,
    },
  };
}
